import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Area, AreaChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { LoadingSpinner } from "../../components/general/LoadingSpinner";

const DAY_CHOICES = [7, 30, 90];

export function withQuery(url, params) {
  const [base, query = ""] = String(url).split("?");
  const search = new URLSearchParams(query);
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value == null || value === "") return;
    search.set(key, String(value));
  });
  const next = search.toString();
  return next ? `${base}?${next}` : base;
}

export function useDayRange() {
  const [params, setParams] = useSearchParams();
  const parsed = Number(params.get("days"));
  const days = DAY_CHOICES.includes(parsed) ? parsed : 30;
  const setDays = (value) => {
    const next = new URLSearchParams(params);
    next.set("days", String(value));
    setParams(next, { replace: true });
  };
  return [days, setDays];
}

export function useApiFilters() {
  const [params, setParams] = useSearchParams();
  const excludeStaff = params.get("exclude_staff") === "1";
  const excludeAnonymous = params.get("exclude_anonymous") === "1";
  const setFlag = (key, checked) => {
    const next = new URLSearchParams(params);
    if (checked) next.set(key, "1");
    else next.delete(key);
    setParams(next, { replace: true });
  };
  return {
    excludeStaff,
    excludeAnonymous,
    setExcludeStaff: (checked) => setFlag("exclude_staff", checked),
    setExcludeAnonymous: (checked) => setFlag("exclude_anonymous", checked),
  };
}

export function usageQuery(days, filters, extra) {
  const excludeStaff = filters === true || Boolean(filters?.excludeStaff);
  const excludeAnonymous = Boolean(filters?.excludeAnonymous);
  return {
    ...(extra || {}),
    ...(days != null ? { days } : {}),
    ...(excludeStaff ? { exclude_staff: 1 } : {}),
    ...(excludeAnonymous ? { exclude_anonymous: 1 } : {}),
  };
}

function FilterCheckbox({ checked, onChange, label }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 rounded border-slate-300"
      />
      {label}
    </label>
  );
}

export function ApiFilterToggles({ excludeStaff, excludeAnonymous, onExcludeStaff, onExcludeAnonymous }) {
  return (
    <>
      <FilterCheckbox checked={excludeStaff} onChange={onExcludeStaff} label="Hide staff calls" />
      <FilterCheckbox checked={excludeAnonymous} onChange={onExcludeAnonymous} label="Hide anonymous calls" />
    </>
  );
}

export function formatCount(value) {
  if (value == null || value === "") return "—";
  const number = Number(value);
  if (Number.isNaN(number)) return String(value);
  return number.toLocaleString();
}

export function formatWhen(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleString();
}

const authHeaders = () => ({
  Authorization: `Token ${localStorage.getItem("authToken")}`,
});

export function useInternalOpsFetch(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasData = useRef(false);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = requestId.current + 1;
    requestId.current = id;
    if (!hasData.current) setLoading(true);
    setError(null);
    try {
      const response = await axios.get(url, { headers: authHeaders() });
      if (id !== requestId.current) return;
      hasData.current = true;
      setData(response.data);
    } catch (err) {
      if (id !== requestId.current || hasData.current) return;
      const detail = err.response?.data?.detail || err.response?.data?.error;
      if (typeof detail === "string") {
        setError(detail);
      } else if (err.response?.status) {
        setError(`Unable to load Internal Ops (${err.response.status}).`);
      } else {
        setError("Unable to load Internal Ops.");
      }
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}

export function InternalOpsPage({ title, subtitle, children }) {
  return (
    <div className="w-full px-4 py-5">
      <div className="mb-8">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Internal Ops</p>
        <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
        {subtitle ? <p className="mt-2 max-w-2xl text-sm text-slate-600">{subtitle}</p> : null}
      </div>
      {children}
    </div>
  );
}

export function InternalOpsState({ loading, error, children }) {
  if (loading) return <LoadingSpinner message="Loading Internal Ops..." />;
  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
        {error}
      </div>
    );
  }
  return children;
}

export function StatCard({ label, value, tone }) {
  const shown = typeof value === "number" ? value.toLocaleString() : value ?? "—";
  const valueClass = tone === "danger" ? "text-red-700" : "text-slate-900";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <p className={`text-xs font-semibold uppercase tracking-wider ${tone === "danger" ? "text-red-700" : "text-slate-500"}`}>
        {label}
      </p>
      <p className={`mt-2 text-2xl font-bold ${valueClass}`}>{shown}</p>
    </div>
  );
}

export function DayRange({ days, onChange }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 text-sm" role="group" aria-label="Date range">
      {DAY_CHOICES.map((value) => (
        <button
          key={value}
          type="button"
          aria-pressed={value === days}
          onClick={() => onChange(value)}
          className={
            value === days
              ? "rounded-md bg-slate-900 px-3 py-1 font-medium text-white"
              : "rounded-md px-3 py-1 text-slate-600 hover:bg-slate-50"
          }
        >
          {value}d
        </button>
      ))}
    </div>
  );
}

function cycleColumnSort(current, key) {
  if (!current || current.key !== key) return { key, direction: "asc" };
  if (current.direction === "asc") return { key, direction: "desc" };
  return null;
}

function SortArrows({ direction }) {
  const up = direction === "asc" ? "#0f172a" : "#cbd5e1";
  const down = direction === "desc" ? "#0f172a" : "#cbd5e1";
  return (
    <svg width="14" height="16" viewBox="0 0 16 8" aria-hidden="true">
      <polygon points="1,3.1 8,0.2 15,3.1" fill={up} />
      <polygon points="1,4.9 8,7.8 15,4.9" fill={down} />
    </svg>
  );
}

function isEmptySortValue(value) {
  return value == null || value === "";
}

function compareSortValues(left, right) {
  if (typeof left === "number" && typeof right === "number") return left - right;
  if (typeof left === "boolean" && typeof right === "boolean") return Number(left) - Number(right);
  const leftDate = typeof left === "string" && /^\d{4}-\d{2}-\d{2}/.test(left) ? Date.parse(left) : NaN;
  const rightDate = typeof right === "string" && /^\d{4}-\d{2}-\d{2}/.test(right) ? Date.parse(right) : NaN;
  if (!Number.isNaN(leftDate) && !Number.isNaN(rightDate)) return leftDate - rightDate;
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: "base" });
}

export const TABLE_PAGE_SIZE = 20;

export function rowsForPage(rows, page, pageSize = TABLE_PAGE_SIZE) {
  const list = rows || [];
  const size = Number(pageSize) > 0 ? Number(pageSize) : TABLE_PAGE_SIZE;
  const number = Math.max(1, Number(page) || 1);
  if (list.length <= size) return list;
  const start = (number - 1) * size;
  return list.slice(start, start + size);
}

export function PageControls({ page = 1, pageCount = 1, onPage, count, pageSize = TABLE_PAGE_SIZE }) {
  const total = Number(count);
  if (!Number.isFinite(total) || total <= 0) return null;
  const size = Number(pageSize) > 0 ? Number(pageSize) : TABLE_PAGE_SIZE;
  const pages = Math.max(Number(pageCount) || 1, Math.ceil(total / size));
  const current = Math.min(Math.max(Number(page) || 1, 1), pages);
  const start = (current - 1) * size + 1;
  const end = Math.min(current * size, total);
  return (
    <div className="flex w-full max-w-full flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
      <span>
        Showing {formatCount(start)}–{formatCount(end)} of {formatCount(total)}
      </span>
      {pages > 1 ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={current <= 1}
            onClick={() => onPage(current - 1)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Previous
          </button>
          <span>
            Page {current} of {pages}
          </span>
          <button
            type="button"
            disabled={current >= pages}
            onClick={() => onPage(current + 1)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function SortableTable({ columns, rows, rowKey, empty = "No rows.", paginate = true }) {
  const [sort, setSort] = useState(null);
  const [page, setPage] = useState(1);
  const sortedRows = useMemo(() => {
    const source = rows || [];
    if (!sort) return source;
    const column = columns.find((item) => item.key === sort.key);
    if (!column) return source;
    const valueOf = column.sortValue || ((row) => row[column.key]);
    return [...source].sort((leftRow, rightRow) => {
      const left = valueOf(leftRow);
      const right = valueOf(rightRow);
      const leftEmpty = isEmptySortValue(left);
      const rightEmpty = isEmptySortValue(right);
      if (leftEmpty && rightEmpty) return 0;
      if (leftEmpty) return 1;
      if (rightEmpty) return -1;
      const compared = compareSortValues(left, right);
      return sort.direction === "desc" ? -compared : compared;
    });
  }, [columns, rows, sort]);
  const pageCount = paginate ? Math.max(1, Math.ceil(sortedRows.length / TABLE_PAGE_SIZE)) : 1;
  const currentPage = paginate ? Math.min(page, pageCount) : 1;
  const visibleRows = paginate
    ? sortedRows.slice((currentPage - 1) * TABLE_PAGE_SIZE, currentPage * TABLE_PAGE_SIZE)
    : sortedRows;

  return (
    <div className="min-w-0 space-y-3">
    <div className="min-w-0 w-full overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>
            {columns.map((column) => {
              const direction = sort?.key === column.key ? sort.direction : null;
              const nextLabel = direction === "asc" ? "highest" : direction === "desc" ? "unsorted" : "lowest";
              return (
                <th key={column.key} className={`px-3 py-1.5 align-bottom ${column.headerClassName || ""}`}>
                  <span className="inline-flex items-center gap-1">
                    <span>{column.label}</span>
                    <button
                      type="button"
                      aria-label={`${column.label}, sort ${nextLabel}`}
                      onClick={() => {
                        setSort((current) => cycleColumnSort(current, column.key));
                        setPage(1);
                      }}
                      className="inline-flex items-center border-0 bg-transparent p-0"
                    >
                      <SortArrows direction={direction} />
                    </button>
                  </span>
                  {column.sublabel ? (
                    <span className="mt-1 block font-mono normal-case tracking-normal text-slate-400">{column.sublabel}</span>
                  ) : null}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row, index) => (
            <tr key={rowKey ? rowKey(row, index) : index} className="border-t border-slate-100">
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={`px-3 py-1.5 ${column.cellClassName || ""}`}
                  title={column.cellTitle ? column.cellTitle(row) : undefined}
                >
                  {column.render ? column.render(row) : row[column.key]}
                </td>
              ))}
            </tr>
          ))}
          {!visibleRows.length ? (
            <tr>
              <td className="px-3 py-1.5 text-slate-500" colSpan={columns.length}>
                {empty}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
    {paginate ? (
      <PageControls page={currentPage} pageCount={pageCount} count={sortedRows.length} onPage={setPage} />
    ) : null}
    </div>
  );
}

const DEFAULT_SERIES = [{ key: "hits", name: "Hits", stroke: "#0f172a", fill: "#e2e8f0" }];

function UsageTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-sm">
      <p className="font-medium text-slate-900">{row.day}</p>
      {payload.map((item) => (
        <p
          key={item.dataKey}
          className={`mt-1 ${item.dataKey === "error_hits" ? "text-red-700" : "text-slate-600"}`}
        >
          {formatCount(item.value)} {String(item.name).toLowerCase()}
        </p>
      ))}
      {row.users != null && !payload.some((item) => item.dataKey === "users") ? (
        <p className="text-slate-600">{formatCount(row.users)} users</p>
      ) : null}
    </div>
  );
}

const LIMIT_COLORS = {
  routes: "#2563eb",
  depots: "#16a34a",
  optimizations: "#7c3aed",
};

function UsageLimitPie({ meter }) {
  const used = Math.max(0, Number(meter.used) || 0);
  const limit = Math.max(0, Number(meter.limit) || 0);
  const remaining = Math.max(0, limit - used);
  const over = limit > 0 && used > limit;
  const color = over ? "#dc2626" : LIMIT_COLORS[meter.key] || "#0f172a";
  const slices = [];
  if (used > 0) slices.push({ name: "Used", value: limit > 0 ? Math.min(used, limit) : used, fill: color });
  if (remaining > 0) slices.push({ name: "Remaining", value: remaining, fill: "#e2e8f0" });
  if (!slices.length) slices.push({ name: "Unused", value: 1, fill: "#e2e8f0" });
  const caption = limit > 0 ? `${formatCount(used)} of ${formatCount(limit)}` : `${formatCount(used)} used`;

  return (
    <div className="min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-3">
      <h3 className="text-center text-sm font-semibold text-slate-900">{meter.title}</h3>
      <div className="mx-auto h-36 w-full max-w-[180px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={slices} dataKey="value" nameKey="name" innerRadius={42} outerRadius={62} stroke="none">
              {slices.map((slice) => (
                <Cell key={slice.name} fill={slice.fill} />
              ))}
            </Pie>
            <Tooltip formatter={(value, name) => [formatCount(name === "Used" ? used : name === "Unused" ? 0 : value), name === "Unused" ? "Used" : name]} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <p className={`text-center text-sm ${over ? "font-medium text-red-700" : "text-slate-700"}`}>{caption}</p>
      <p className="text-center text-xs text-slate-500">
        {limit > 0 ? `${formatCount(remaining)} remaining` : "No limit set"}
      </p>
    </div>
  );
}

export function UsageLimitCharts({ limits }) {
  const meters = limits || [];
  if (!meters.length) return null;
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold text-slate-900">Usage and limits</h2>
      <div className="grid gap-3 sm:grid-cols-3">
        {meters.map((meter) => (
          <UsageLimitPie key={meter.key} meter={meter} />
        ))}
      </div>
    </section>
  );
}

export function UsageChart({
  rows,
  title = "Usage over time",
  caption = "Daily hits, UTC.",
  series = DEFAULT_SERIES,
  empty = "No calls in this window.",
}) {
  const data = (rows || []).map((row) => ({
    ...row,
    label: String(row.day).slice(5),
  }));
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      {caption ? <p className="mt-1 text-xs text-slate-500">{caption}</p> : null}
      {data.length ? (
        <div className="mt-4 h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#e2e8f0" vertical={false} />
              <XAxis
                dataKey="label"
                minTickGap={28}
                tick={{ fontSize: 12, fill: "#64748b" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                width={40}
                tick={{ fontSize: 12, fill: "#64748b" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<UsageTooltip />} />
              {series.length > 1 ? <Legend /> : null}
              {series.map((item) => (
                <Area
                  key={item.key}
                  type="monotone"
                  dataKey={item.key}
                  name={item.name}
                  stroke={item.stroke}
                  fill={item.fill}
                  strokeWidth={2}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="mt-4 text-sm text-slate-500">{empty}</p>
      )}
    </section>
  );
}
